/**
 * TabbedHub — a generic tabbed container for HR "hub" pages.
 *
 * Consolidates several previously-separate HR nav items into one sidebar
 * entry whose sub-surfaces live behind in-page tabs (the pattern used by
 * /hr/org-structure). Each tab renders an existing full page component
 * inline — the child brings its own PageHead + HrAccessGuard, so the hub
 * adds only the tab strip on top and does not duplicate page chrome.
 *
 * Tabs whose feature flag is OFF are dropped. The active tab round-trips
 * through the `?tab=` search param so it deep-links and survives refresh;
 * an unknown/absent `tab` falls back to the first enabled tab.
 *
 * Access control (RequireRole) stays on the hub's ROUTE; each child also
 * wraps itself in HrAccessGuard, which is a pure pass-through gate, so the
 * nesting is harmless.
 */
import { useCallback, useMemo, type CSSProperties, type ReactNode } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { flags, type FeatureFlagKey } from '../../lib/feature-flags'
import { PageHead, HubChromeProvider } from '../../components/primitives/PageHead'

export interface HubTab {
  /** URL-safe slug persisted in `?tab=` and used for test ids. */
  key: string
  label: string
  /** Optional gate — the tab is omitted when the flag is OFF. */
  flag?: FeatureFlagKey
  render: () => ReactNode
}

// No-line tabs, matching /hr/org-structure: active = colour + weight + a
// 2px underline. Toggle the SAME `borderBottom` shorthand the base sets
// (not the longhand) so React's style diff never leaves a stale colour.
const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: 18,
  marginBottom: 20,
  flexWrap: 'wrap',
}
const tabButtonStyle: CSSProperties = {
  padding: '4px 0 6px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  color: 'var(--text-3)',
  transition: 'color 120ms ease, border-color 120ms ease',
}
const tabButtonActiveStyle: CSSProperties = {
  color: 'var(--steel-blue)',
  fontWeight: 700,
  borderBottom: '2px solid var(--steel-blue)',
}

interface TabbedHubProps {
  tabs: HubTab[]
  /** Prefix for test ids, e.g. "import-export" → `hub-tab-import-export-<key>`. */
  testIdPrefix: string
  /** The hub's own route path — the target for `?tab=` navigation. */
  routePath: '/hr/import-export' | '/hr/adjustments' | '/hr/leaves' | '/hr/coverage'
  /** Hero heading, owned by the hub. Each tab's child page then renders only
   *  its own subtitle beneath the tab bar (see HubChromeProvider / PageHead). */
  heroTitle: string
  heroEyebrow?: string
}

export function TabbedHub({
  tabs,
  testIdPrefix,
  routePath,
  heroTitle,
  heroEyebrow = 'HR',
}: TabbedHubProps): JSX.Element | null {
  const search = useSearch({ strict: false }) as { tab?: string }
  const navigate = useNavigate()

  const enabled = useMemo(
    () => tabs.filter((t) => t.flag == null || flags[t.flag]),
    [tabs],
  )

  const activeKey =
    enabled.find((t) => t.key === search.tab)?.key ?? enabled[0]?.key ?? null

  const selectTab = useCallback(
    (key: string) => {
      // Merge so any child-owned search params survive the tab switch.
      void navigate({ to: routePath, search: { ...search, tab: key }, replace: true })
    },
    [navigate, routePath, search],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
      e.preventDefault()
      const delta = e.key === 'ArrowRight' ? 1 : -1
      const next = enabled[(index + delta + enabled.length) % enabled.length]
      if (next) selectTab(next.key)
    },
    [enabled, selectTab],
  )

  // No enabled tabs (every flag off) → render nothing so the route falls
  // through to the not-found surface, same as a flag-gated page returning null.
  if (activeKey == null) return null
  const active = enabled.find((t) => t.key === activeKey) ?? enabled[0]!

  return (
    <>
      <PageHead eyebrow={heroEyebrow} title={heroTitle} />
      <div role="tablist" style={tabListStyle}>
        {enabled.map((t, i) => {
          const isActive = t.key === activeKey
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              data-testid={`hub-tab-${testIdPrefix}-${t.key}`}
              onClick={() => selectTab(t.key)}
              onKeyDown={(e) => onKeyDown(e, i)}
              style={{ ...tabButtonStyle, ...(isActive ? tabButtonActiveStyle : {}) }}
            >
              {t.label}
            </button>
          )
        })}
      </div>
      {/* Inside the hub, each child page's PageHead collapses to subtitle-only,
          so the tab's description sits right under the tab bar. */}
      <HubChromeProvider>
        <div role="tabpanel" data-testid={`hub-panel-${testIdPrefix}-${active.key}`}>
          {active.render()}
        </div>
      </HubChromeProvider>
    </>
  )
}
